import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, Building2, Phone, Megaphone, BookOpen, BarChart3, Heart } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";

const Bilingual = ({ label, value, onChange, textarea = false, rows = 3, hint }) => {
  const Input = textarea ? "textarea" : "input";
  const v = value || { en: "", ar: "" };
  return (
    <div>
      {label && <div className="text-xs font-semibold text-slate-700 mb-1.5">{label} {hint && <span className="text-slate-400 font-normal">— {hint}</span>}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">English</span>
          <Input
            {...(textarea ? { rows } : {})}
            value={v.en || ""}
            onChange={(e) => onChange({ ...v, en: e.target.value })}
            className="admin-input mt-1 resize-none"
          />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">العربية</span>
          <Input
            {...(textarea ? { rows } : {})}
            dir="rtl"
            value={v.ar || ""}
            onChange={(e) => onChange({ ...v, ar: e.target.value })}
            className="admin-input mt-1 resize-none"
          />
        </div>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, description, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-200 flex items-start gap-3">
      <div className="w-9 h-9 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <h2 className="font-semibold text-slate-900">{title}</h2>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="p-6 space-y-5">{children}</div>
  </section>
);

const TABS = [
  { key: "company", label: "Company", icon: Building2 },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "hero", label: "Hero", icon: Megaphone },
  { key: "about", label: "About", icon: BookOpen },
  { key: "stats", label: "Stats", icon: BarChart3 },
  { key: "values", label: "Values", icon: Heart },
];

const AdminContent = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/site-content");
      setContent(res.data || {});
    } catch { toast.error("Failed to load content"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", content);
      toast.success("All changes saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setContent((c) => ({ ...c, [key]: value }));

  if (loading || !content) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-10 w-64 rounded" />
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-40 rounded-xl" />)}
      </div>
    );
  }

  const phones = content.phones || [];
  const stats = content.stats || [];
  const values = content.values || [];

  return (
    <div data-testid="admin-content-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Site Content</h1>
          <p className="mt-1 text-sm text-slate-500">Edit the content displayed across your public website.</p>
        </div>
        <button onClick={save} disabled={saving} className="admin-btn-primary" data-testid="content-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg overflow-x-auto admin-scroll">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === tab.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid={`content-tab-${tab.key}`}
          >
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {activeTab === "company" && (
          <Section icon={Building2} title="Company" description="Identity and branding details.">
            <Bilingual label="Company Name" value={content.company_name} onChange={(v) => update("company_name", v)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Short Name</label>
                <input value={content.short_name || ""} onChange={(e) => update("short_name", e.target.value)} className="admin-input" data-testid="content-short-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">CR Number</label>
                <input value={content.cr_number || ""} onChange={(e) => update("cr_number", e.target.value)} className="admin-input" />
              </div>
            </div>
            <Bilingual label="Tagline" value={content.tagline} onChange={(v) => update("tagline", v)} textarea rows={2} />
            <Bilingual label="Location" value={content.location} onChange={(v) => update("location", v)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Logo</label>
                <ImageUpload value={content.logo} onChange={(url) => update("logo", url)} testId="content-logo" aspectClass="w-24 h-24" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Services Page — Featured "01" Image</label>
                <p className="text-[10px] text-slate-500 mb-2">Shown on the /services page as the featured rental visual.</p>
                <ImageUpload value={content.services_featured_image} onChange={(url) => update("services_featured_image", url)} testId="content-services-featured-image" aspectClass="w-40 h-32" />
              </div>
            </div>
          </Section>
        )}

        {activeTab === "contact" && (
          <Section icon={Phone} title="Contact" description="Email and phone numbers shown across the site.">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email</label>
              <input value={content.email || ""} onChange={(e) => update("email", e.target.value)} className="admin-input" data-testid="content-email" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-700">Phone Numbers</label>
                <button onClick={() => update("phones", [...phones, ""])} className="admin-btn-secondary text-xs"><Plus size={12} /> Add phone</button>
              </div>
              <div className="space-y-2">
                {phones.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={p} onChange={(e) => { const arr = [...phones]; arr[i] = e.target.value; update("phones", arr); }} className="admin-input flex-1" />
                    <button onClick={() => update("phones", phones.filter((_, idx) => idx !== i))} className="admin-btn-danger"><Trash2 size={12} /></button>
                  </div>
                ))}
                {phones.length === 0 && <p className="text-xs text-slate-400">No phone numbers yet.</p>}
              </div>
            </div>
          </Section>
        )}

        {activeTab === "hero" && (
          <Section icon={Megaphone} title="Hero (Home Page)" description="The headline visitors see first.">
            <Bilingual label="Hero Title" value={content.hero_title} onChange={(v) => update("hero_title", v)} textarea rows={2} />
            <Bilingual label="Hero Subtitle" value={content.hero_subtitle} onChange={(v) => update("hero_subtitle", v)} textarea rows={3} />
          </Section>
        )}

        {activeTab === "about" && (
          <Section icon={BookOpen} title="About Page" description="Mission, vision, and the story behind RTC.">
            <Bilingual label="About Title" value={content.about_title} onChange={(v) => update("about_title", v)} />
            <Bilingual label="About Body" value={content.about_body} onChange={(v) => update("about_body", v)} textarea rows={6} />
            <Bilingual label="Mission" value={content.mission} onChange={(v) => update("mission", v)} textarea rows={4} />
            <Bilingual label="Vision" value={content.vision} onChange={(v) => update("vision", v)} textarea rows={4} />
          </Section>
        )}

        {activeTab === "stats" && (
          <Section icon={BarChart3} title="Stats" description="Numerical highlights shown on Home and About.">
            <div className="space-y-3">
              {stats.map((s, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Value</label>
                      <input value={s.value || ""} onChange={(e) => { const arr = [...stats]; arr[i] = { ...s, value: e.target.value }; update("stats", arr); }} className="admin-input bg-white" placeholder="50+" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Label EN</label>
                      <input value={s.label_en || ""} onChange={(e) => { const arr = [...stats]; arr[i] = { ...s, label_en: e.target.value }; update("stats", arr); }} className="admin-input bg-white" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Label AR</label>
                      <input dir="rtl" value={s.label_ar || ""} onChange={(e) => { const arr = [...stats]; arr[i] = { ...s, label_ar: e.target.value }; update("stats", arr); }} className="admin-input bg-white" />
                    </div>
                    <button onClick={() => update("stats", stats.filter((_, idx) => idx !== i))} className="admin-btn-danger md:col-span-1"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => update("stats", [...stats, { value: "", label_en: "", label_ar: "" }])} className="admin-btn-secondary"><Plus size={14} /> Add Stat</button>
          </Section>
        )}

        {activeTab === "values" && (
          <Section icon={Heart} title="Values" description="Brand pillars displayed on Home and About.">
            <div className="space-y-3">
              {values.map((v, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input placeholder="Title (English)" value={v.title_en || ""} onChange={(e) => { const arr = [...values]; arr[i] = { ...v, title_en: e.target.value }; update("values", arr); }} className="admin-input bg-white" />
                    <input dir="rtl" placeholder="العنوان (عربي)" value={v.title_ar || ""} onChange={(e) => { const arr = [...values]; arr[i] = { ...v, title_ar: e.target.value }; update("values", arr); }} className="admin-input bg-white" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <textarea rows={2} placeholder="Description (English)" value={v.text_en || ""} onChange={(e) => { const arr = [...values]; arr[i] = { ...v, text_en: e.target.value }; update("values", arr); }} className="admin-input bg-white resize-none" />
                    <textarea dir="rtl" rows={2} placeholder="الوصف (عربي)" value={v.text_ar || ""} onChange={(e) => { const arr = [...values]; arr[i] = { ...v, text_ar: e.target.value }; update("values", arr); }} className="admin-input bg-white resize-none" />
                  </div>
                  <button onClick={() => update("values", values.filter((_, idx) => idx !== i))} className="admin-btn-danger text-xs"><Trash2 size={12} /> Remove</button>
                </div>
              ))}
            </div>
            <button onClick={() => update("values", [...values, { title_en: "", title_ar: "", text_en: "", text_ar: "" }])} className="admin-btn-secondary"><Plus size={14} /> Add Value</button>
          </Section>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="admin-btn-primary shadow-lg shadow-orange-500/30 px-6 py-3" data-testid="content-save-btn-bottom">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save All Changes
        </button>
      </div>
    </div>
  );
};

export default AdminContent;
